import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import MathText from "./MathText.jsx";
import PdfPages from "./PdfPages.jsx";
import "./MarkedTests.css";

const TERMS = [1, 2, 3, 4];
const endpoint = () => `${process.env.REACT_APP_API_URL}/marked_scripts.php`;

const formatMark = (value) => {
  const mark = Number(value || 0);
  return Number.isInteger(mark)
    ? String(mark)
    : mark.toFixed(2).replace(/0+$/, "").replace(/\.$/, "");
};

const formatDate = (value) => {
  const date = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(date.getTime())
    ? ""
    : date.toLocaleDateString("en-ZA", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
};

const fullName = (run) => `${run.first_name} ${run.last_name}`.trim();

export const markbookChangeNotice = (markbook) => {
  if (!markbook?.changed) return "";
  return markbook.current === null
    ? "This mark has since been removed from the markbook."
    : `The markbook now shows ${formatMark(markbook.current)}, changed from the ${formatMark(markbook.recorded)} saved with this marking.`;
};

function ScriptViewer({ run }) {
  const [scriptUrl, setScriptUrl] = useState("");
  const [scriptBlob, setScriptBlob] = useState(null);
  const [state, setState] = useState({ loading: run.has_script, error: "" });
  const [attempt, setAttempt] = useState(0);

  useEffect(
    () => () => {
      if (scriptUrl) URL.revokeObjectURL(scriptUrl);
    },
    [scriptUrl],
  );

  // The ticked PDF is what teachers come here for, so it opens straight away.
  useEffect(() => {
    if (!run.has_script) return undefined;
    let active = true;
    setState({ loading: true, error: "" });
    axios
      .get(endpoint(), {
        params: { run_id: run.run_id, file: 1 },
        responseType: "blob",
      })
      .then(({ data }) => {
        if (!active) return;
        const blob = new Blob([data], { type: "application/pdf" });
        setScriptBlob(blob);
        setScriptUrl(URL.createObjectURL(blob));
        setState({ loading: false, error: "" });
      })
      .catch(() => {
        if (active) setState({ loading: false, error: "The marked script could not be opened." });
      });
    return () => {
      active = false;
    };
  }, [run.run_id, run.has_script, attempt]);

  const previewFailed = useCallback(() => {
    setState({ loading: false, error: "The preview could not be shown, but you can still download the PDF." });
  }, []);

  if (!run.has_script) {
    return (
      <p className="marked-tests-empty marked-tests-note">
        {run.script_removed
          ? `The script copy was deleted after its retention date (${formatDate(run.script_expires_at)}). The question breakdown stays available.`
          : "No script copy is stored for this test, only the question breakdown."}
      </p>
    );
  }
  return (
    <div className="marked-tests-script">
      <div className="marked-tests-toolbar">
        <p className="marked-tests-note">
          Copy kept until {formatDate(run.script_expires_at)}
        </p>
        {scriptUrl && (
          <a
            className="marked-tests-download"
            href={scriptUrl}
            download={`${fullName(run)} - ${run.label}.pdf`}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 3v12 M7 10l5 5 5-5 M4 20h16" />
            </svg>
            Download PDF
          </a>
        )}
      </div>
      {scriptUrl && (
        <PdfPages blob={scriptBlob} title={`Marked script for ${fullName(run)}`} onError={previewFailed} />
      )}
      {state.loading && <p className="marked-tests-empty">Opening the marked script…</p>}
      {state.error && (
        <div className="marked-tests-empty">
          <p className="marked-tests-error" role="alert">
            {state.error}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setAttempt((value) => value + 1)}
          >
            Try again
          </button>
        </div>
      )}
    </div>
  );
}

function MarkedTestDetail({ runId, onSelectRun, onBack }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setDetail(null);
    setError("");
    axios
      .get(endpoint(), { params: { run_id: runId } })
      .then(({ data }) => {
        if (active) setDetail(data);
      })
      .catch(() => {
        if (active) setError("This marked test could not be loaded.");
      });
    return () => {
      active = false;
    };
  }, [runId]);

  const back = (
    <button type="button" className="marked-tests-back" onClick={onBack}>
      ← All marked tests
    </button>
  );
  if (error) {
    return (
      <div className="marked-tests-detail">
        {back}
        <p className="marked-tests-error" role="alert">
          {error}
        </p>
      </div>
    );
  }
  if (!detail) return <p className="marked-tests-note">Loading marked test…</p>;

  const { run, history } = detail;
  const isLatest = history[0]?.run_id === run.run_id;
  const changeNotice = isLatest ? markbookChangeNotice(run.markbook) : "";

  return (
    <div className="marked-tests-detail">
      {back}
      <header className="marked-tests-head">
        <div>
          <h3>{fullName(run)}</h3>
          <p>
            {run.label} · Term {run.term}
          </p>
        </div>
        <p className="marked-tests-score">
          <strong>
            {formatMark(run.total_awarded)}
            <span>/{formatMark(run.total_marks)}</span>
          </strong>
          {run.total_marks > 0 && (
            <span>{Math.round((run.total_awarded / run.total_marks) * 100)}%</span>
          )}
        </p>
      </header>

      <div className="marked-tests-review">
        <ScriptViewer key={run.run_id} run={run} />

        <aside className="marked-tests-side">
          {changeNotice && (
            <p className="marked-tests-warning" role="status">
              {changeNotice}
            </p>
          )}
          {!isLatest && (
            <p className="marked-tests-warning" role="status">
              This is an earlier marking. A newer one replaced it.
            </p>
          )}
          <p className="marked-tests-note">
            Approved {formatDate(run.created_at)}
            {run.approved_by_name
              ? ` by ${run.approved_by_name}`
              : " by a teacher no longer at the school"}
          </p>

      {history.length > 1 && (
        <section aria-label="Marking history">
          <h4>Marking history</h4>
          <ul className="marked-tests-history">
            {history.map((entry) => (
              <li key={entry.run_id}>
                <button
                  type="button"
                  aria-current={entry.run_id === run.run_id ? "true" : undefined}
                  onClick={() => onSelectRun(entry.run_id)}
                >
                  {formatDate(entry.created_at)} ·{" "}
                  {formatMark(entry.total_awarded)}/
                  {formatMark(entry.total_marks)}
                </button>
              </li>
            ))}
          </ul>
        </section>
      )}

      <details className="marked-tests-breakdown" aria-label="Question breakdown">
        <summary>Question breakdown <span>{run.items.length}</span></summary>
        {run.items.map((item, index) => (
          <article className="marked-tests-question" key={`${item.question_number}-${index}`}>
            <div className="marked-tests-question-head">
              <strong>Question {item.question_number}</strong>
              <span>
                {formatMark(item.marks_awarded)}/{formatMark(item.max_marks)}
              </span>
            </div>
            {item.learner_answer && (
              <p>
                <span>Learner's answer</span>
                <MathText>{item.learner_answer}</MathText>
              </p>
            )}
            {item.correct_answer && (
              <p>
                <span>Expected answer</span>
                <MathText>{item.correct_answer}</MathText>
              </p>
            )}
            {item.feedback && (
              <p>
                <span>Feedback</span>
                <MathText>{item.feedback}</MathText>
              </p>
            )}
            {item.method_steps.length > 0 && (
              <details>
                <summary>Steps ({item.method_steps.length})</summary>
                <ul>
                  {item.method_steps.map((step, stepIndex) => (
                    <li key={`${step.step}-${stepIndex}`}>
                      <strong>
                        <MathText>{step.step}</MathText>:
                      </strong>{" "}
                      {formatMark(step.marksAwarded)}/{formatMark(step.maxMarks)}
                      {step.feedback ? (
                        <>
                          {" "}
                          <MathText>{step.feedback}</MathText>
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </article>
        ))}
      </details>
        </aside>
      </div>
    </div>
  );
}

export default function MarkedTests({ subjectId, initialTerm, initialRunId = null }) {
  const [term, setTerm] = useState(initialTerm);
  const [runId, setRunId] = useState(initialRunId);
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    setRuns(null);
    setError("");
    axios
      .get(endpoint(), { params: { subject_id: subjectId, term } })
      .then(({ data }) => {
        if (active) setRuns(Array.isArray(data?.runs) ? data.runs : []);
      })
      .catch(() => {
        if (active) setError("Marked tests could not be loaded.");
      });
    return () => {
      active = false;
    };
  }, [subjectId, term]);

  const assessments = useMemo(() => {
    const grouped = new Map();
    (runs || []).forEach((run) => {
      if (!grouped.has(run.label)) grouped.set(run.label, []);
      grouped.get(run.label).push(run);
    });
    grouped.forEach((list) =>
      list.sort((a, b) => fullName(a).localeCompare(fullName(b))),
    );
    return [...grouped.entries()];
  }, [runs]);

  if (runId) {
    return (
      <div className="marked-tests">
        <MarkedTestDetail
          runId={runId}
          onSelectRun={setRunId}
          onBack={() => setRunId(null)}
        />
      </div>
    );
  }

  return (
    <div className="marked-tests">
      <div className="marked-tests-terms" role="group" aria-label="Term">
        {TERMS.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={value === term}
            onClick={() => setTerm(value)}
          >
            Term {value}
          </button>
        ))}
      </div>

      {error && (
        <p className="marked-tests-error" role="alert">
          {error}
        </p>
      )}
      {!error && runs === null && (
        <p className="marked-tests-note">Loading marked tests…</p>
      )}
      {runs?.length === 0 && (
        <p className="marked-tests-note">
          No tests were marked with Automated Marking in Term {term}.
        </p>
      )}

      {assessments.map(([label, list]) => (
        <details className="marked-tests-assessment" key={label} open={assessments.length === 1}>
          <summary>
            {label} <span>{list.length} marked</span>
          </summary>
          <ul>
            {list.map((run) => (
              <li key={run.run_id}>
                <button type="button" onClick={() => setRunId(run.run_id)}>
                  <span className="marked-tests-learner">{fullName(run)}</span>
                  <span>
                    {formatMark(run.total_awarded)}/{formatMark(run.total_marks)}
                  </span>
                  <span className="marked-tests-meta">
                    {formatDate(run.created_at)}
                    {run.attempts > 1 ? ` · marked ${run.attempts} times` : ""}
                    {run.markbook?.changed ? " · markbook changed" : ""}
                    {run.has_script ? "" : " · breakdown only"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}
